import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { handleAllErrors, handleErrors, ShowErrorNotification, ShowSuccessNotification } from '@platform-frontend/util';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { ACCOUNT_ERROR_HANDLERS } from '../account-error-handlers';
import { MSG_DISABLED_DETAILS_DEFAULT, MSG_REACTIVATED_ACCOUNT } from '../notification-messages';
import { Session, SessionState, SetSession, SignOutOnError } from '../session';
import { FetchDisabledDetails, MarkAccountForDeletion, ReactivateAccount } from './disabled-details.actions';
import { DisabledDetailsStateModel, SerializedDisabledDetails } from './model/disabled-details-state-model';
import { DisabledReason } from './model/disabled-reason';

const DEFAULT_STATE: DisabledDetailsStateModel = {
  deleteDate: null,
  disabledDate: null,
  reason: null,
  upcomingPayment: false
};

@State<DisabledDetailsStateModel>({
  name: 'disabledDetails',
  defaults: DEFAULT_STATE
})
@Injectable()
export class DisabledDetailsState {
  constructor(private http: HttpClient, private store: Store) {}

  isDisabled$: Observable<boolean | null> = this.store.select(SessionState.isDisabled);

  @Selector()
  static deleteDate(state: DisabledDetailsStateModel): Date | null {
    return state.deleteDate;
  }

  @Selector()
  static disabledDate(state: DisabledDetailsStateModel): Date | null {
    return state.disabledDate;
  }

  @Selector()
  static reason(state: DisabledDetailsStateModel): DisabledReason | null {
    return state.reason;
  }

  @Selector()
  static upcomingPayment(state: DisabledDetailsStateModel): boolean {
    return state.upcomingPayment;
  }

  @Action(FetchDisabledDetails)
  fetchDisabledDetails(ctx: StateContext<DisabledDetailsStateModel>): Observable<unknown> {
    return this.isDisabled$.pipe(
      take(1),
      switchMap(isDisabled => (isDisabled ? this.getDisabledDetails(ctx) : this.resetState(ctx)))
    );
  }

  private getDisabledDetails(ctx: StateContext<DisabledDetailsStateModel>): Observable<unknown> {
    return this.http.get<SerializedDisabledDetails>(PLATFORM_ENVIRONMENT.apiUrl + '/account/accountDisabledDetails').pipe(
      tap(details =>
        ctx.patchState({ ...details, deleteDate: this.convertToDate(details.deleteDate), disabledDate: this.convertToDate(details.disabledDate) })
      ),
      handleAllErrors(new SignOutOnError(new ShowErrorNotification(MSG_DISABLED_DETAILS_DEFAULT))),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_DISABLED_DETAILS_DEFAULT) // ACCOUNT_NOT_DISABLED, NOT_FOUND
      })
    );
  }

  private convertToDate(serializedDate: string | null): Date | null {
    return serializedDate ? new Date(serializedDate) : null;
  }

  @Action(SetSession)
  resetStateIfAccountEnabled(ctx: StateContext<DisabledDetailsStateModel>, { session }: SetSession): Observable<unknown> {
    return session?.disabled ? EMPTY : this.resetState(ctx);
  }

  private resetState(ctx: StateContext<DisabledDetailsStateModel>): Observable<unknown> {
    ctx.patchState(DEFAULT_STATE);
    return EMPTY;
  }

  @Action(ReactivateAccount)
  reactivate(ctx: StateContext<DisabledDetailsStateModel>): Observable<unknown> {
    return this.http.post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/account/reactivate', {}).pipe(
      switchMap(() => ctx.dispatch(new ShowSuccessNotification(MSG_REACTIVATED_ACCOUNT))),
      handleErrors(ACCOUNT_ERROR_HANDLERS)
    );
  }

  @Action(MarkAccountForDeletion)
  markForDeletion(ctx: StateContext<DisabledDetailsStateModel>, { reason }: MarkAccountForDeletion): Observable<unknown> {
    return this.http.post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/account/markForDeletion', { reason }).pipe(
      handleErrors(ACCOUNT_ERROR_HANDLERS),
      switchMap(() => ctx.dispatch(new FetchDisabledDetails()))
    );
  }
}
